import { SyncTaskDaySummary, SyncTaskResult, SyncTaskState, SyncTaskStatus } from 'services/busmanApi';

export const POLL_INTERVAL_MS = 2000;

// Stop chasing a task that never reports a terminal state
export const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * The payroll tasks sweep one date at a time and every date can stall on a slow MYOB
 * read, so their ceiling has to scale with the range instead of sitting at a flat value.
 * The backend bound is dates x pages x 60s, with a page per 400 timesheets on a date.
 */
export const taskTimeoutForDays = (days: number) => Math.max(2 * 60 * 1000, days * 70 * 1000);

const TERMINAL_STATES: SyncTaskState[] = ['SUCCESS', 'FAILURE', 'REVOKED'];

export const isTerminalState = (state?: SyncTaskState) => !!state && TERMINAL_STATES.includes(state);

// The payroll tasks report `current`/`total`; the older syncs report celery meta either
// flat or nested under `progress`
export const getPercent = (status?: SyncTaskStatus) => {
  if (!status) return 0;

  const percent = status.percent ?? status.progress?.percent;
  if (typeof percent === 'number') return Math.min(100, Math.max(0, percent));

  const current = status.current ?? status.progress?.current;
  const total = status.total ?? status.progress?.total;
  if (typeof current === 'number' && typeof total === 'number' && total > 0) {
    return Math.min(100, Math.round((current / total) * 100));
  }

  return 0;
};

// Renders the tallies that actually happened, e.g. "42 fetched, 12 processed"
const formatCounts = (counts: Record<string, number | undefined>) =>
  Object.entries(counts)
    .filter(([, value]) => typeof value === 'number' && value > 0)
    .map(([name, value]) => `${value} ${name}`)
    .join(', ');

/**
 * The pull reports a date's tallies under `last_day` and the adjustment under
 * `last_adjustment`. The adjustment also throttles to every tenth pair, so its summaries
 * arrive in jumps rather than one per date.
 */
export const getLastDay = (status: SyncTaskStatus): SyncTaskDaySummary | undefined =>
  status.last_day ?? status.last_adjustment;

/**
 * Synced against what is still outstanding, e.g. "12 synced, 2 pending of 14". `synced`
 * counts the items written, while `pending` comes off the task's own `current`/`total`
 * progress counter rather than off `processed`.
 */
export const getSyncedVsPending = (status: SyncTaskStatus) => {
  const { current, total } = status;
  const pending = typeof current === 'number' && typeof total === 'number' ? Math.max(0, total - current) : undefined;

  const tally = formatCounts({
    synced: status.processed ?? status.adjusted,
    pending,
    failed: status.failed,
  });

  if (!tally) return '';

  return typeof total === 'number' ? `${tally} of ${total}` : tally;
};

/**
 * PROGRESS frames from the payroll tasks carry no top level message, so the line is built
 * from the date being swept and how much of the run is done.
 */
export const getTaskProgressMessage = (status: SyncTaskStatus, fallback: string) => {
  if (status.message) return status.message;

  const date = status.current_date ?? getLastDay(status)?.date;
  const head = date ? `${fallback} - ${date}` : fallback;
  const tally = getSyncedVsPending(status);

  return tally ? `${head} [${tally}]` : head;
};

// One log line for a date the sweep has finished with. Only a failed date carries a message.
export const getDayLogLine = (day: SyncTaskDaySummary, label: string) => {
  if (day.sync_success === false) return `Error ${label} for ${day.date}: ${day.message}`;

  const counts = formatCounts({
    fetched: day.fetched,
    filtered: day.filtered,
    processed: day.processed ?? day.adjusted,
    skipped: day.skipped,
    failed: day.failed,
  });

  return `${label} ${day.date}: ${counts || 'nothing to sync'}`;
};

// The closing tally, from `result.counts` on a task that ran to completion
export const getResultLogLine = (status: SyncTaskStatus, label: string) => {
  const counts = formatCounts(status.result?.counts ?? {});
  if (!counts) return `Finished ${label}`;

  const total = typeof status.result?.total === 'number' ? ` of ${status.result.total}` : '';

  return `Finished ${label}: ${counts}${total}`;
};

export type TaskOutcome =
  | { type: 'success'; message?: string }
  | { type: 'skipped'; message: string }
  | { type: 'error'; message: string };

/**
 * A celery SUCCESS only means the task ran to completion. The payroll tasks report their
 * own verdict in `result.success` - a sweep with a failed day, or an employee id that
 * could not be resolved, still completes as SUCCESS. `result.skipped` means the lock was
 * held by another sync, which is neither a success nor an error.
 */
export const getTaskOutcome = (status: SyncTaskStatus, label: string): TaskOutcome => {
  if (status.state !== 'SUCCESS') {
    return { type: 'error', message: status.error || `${label} failed` };
  }

  const result: SyncTaskResult | undefined = status.result;

  if (result?.skipped === true) {
    return { type: 'skipped', message: result.message || `${label} is already running` };
  }

  if (result?.success === false) {
    return { type: 'error', message: result.message || `${label} did not complete` };
  }

  return { type: 'success', message: result?.message };
};

/**
 * Failed requests are shaped `{ success: false, errors: '<message>' }` - note the key is
 * plural and holds a plain string, not the field map the name suggests.
 */
export const getApiErrorMessage = (error: unknown, fallback: string) => {
  const errors = (error as { data?: { errors?: unknown } } | undefined)?.data?.errors;
  return typeof errors === 'string' && errors ? errors : fallback;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type PollTrigger = (taskId: string) => { unwrap: () => Promise<SyncTaskStatus> };

/**
 * Imperative counterpart to `useMyobTaskSync`, for sync calls that happen inside a loop
 * where a hook driven poller cannot be used. Resolves once the task reaches a terminal
 * state; rejects if the status request fails or the task outlives `timeoutMs`.
 */
export async function pollSyncTask(
  trigger: PollTrigger,
  taskId: string,
  onProgress?: (status: SyncTaskStatus) => void,
  timeoutMs: number = POLL_TIMEOUT_MS,
): Promise<SyncTaskStatus> {
  const deadline = Date.now() + timeoutMs;

  for (;;) {
    const status = await trigger(taskId).unwrap();
    onProgress?.(status);

    if (isTerminalState(status.state)) return status;

    if (Date.now() > deadline) {
      throw new Error(`Timed out waiting for sync task ${taskId}`);
    }

    await delay(POLL_INTERVAL_MS);
  }
}
